import { createInstance } from 'i18next';
import Backend, { type HttpBackendOptions } from 'i18next-http-backend';

import { report, REPORT_TYPES } from '@adsk/exo-skeleton-api';

import { EXO_SKELETON_SCOPE_NAME } from '../consts';
import { supportedLocales } from '../locale/consts';

const i18nInstance = createInstance();

const defaultLanguage = 'en';

// eslint-disable-next-line @typescript-eslint/no-require-imports
const defaultLocaleValue: Record<string, string> = require(
  `../locale/${defaultLanguage}/translation.json`,
);

// Normalization dictionary for more complicated languages / cultures combinations
const normalizeLangDict = {
  // simplified Chinese
  zh: 'zh-Hans',
  'zh-CN': 'zh-Hans',
  'zh-TW': 'zh-Hant',
  'zh-HK': 'zh-Hant-HK',
} as Record<string, string>;

const getNormalizeLang = (lang: string): string => {
  return normalizeLangDict[lang] || lang;
};

/**
 * Find similar languages (pt-BR -> pt)
 * @param lang
 * @return {string}
 */
const getSimilarLang = (lang: string): string => {
  // Chinese have a special logic in `normalizeLangDict`
  if (lang.startsWith('zh')) {
    return lang;
  }
  return lang.substring(0, lang.indexOf('-'));
};

const getBrowserLanguage = (): string => {
  // Safari 9.1.1 has no support for navigator.languages, we only support latest version

  const languages = navigator.languages || [
    navigator.language || defaultLanguage,
  ];
  const userLanguages = [...languages];

  for (const lang of userLanguages) {
    const normalizeLang = getNormalizeLang(lang);
    if (supportedLocales.includes(normalizeLang)) {
      return normalizeLang;
    }
    const similarLang = getSimilarLang(lang);
    if (supportedLocales.includes(similarLang)) {
      return similarLang;
    }
  }

  return defaultLanguage;
};

export const initi18n = async () => {
  let language;
  try {
    language = getBrowserLanguage();

    const fileRequest: HttpBackendOptions['request'] = (
      _options,
      url,
      _payload,
      callback,
    ) => {
      const [lang, namespace] = url.split('|');

      if (lang === defaultLanguage) {
        callback(null, {
          data: JSON.stringify(defaultLocaleValue),
          status: 200,
        });
        return;
      }

      // eslint-disable-next-line no-unsanitized/method
      import(`../locale/${lang}/${namespace}.json`)
        .then((data) => {
          callback(null, {
            data: JSON.stringify(data),
            status: 200,
          });
        })
        .catch((error) => {
          callback(error, {
            data: JSON.stringify({}),
            status: 404,
          });
        });
    };

    await i18nInstance.use(Backend).init({
      fallbackLng: defaultLanguage,
      lng: language,
      backend: {
        loadPath: '{{lng}}|{{ns}}',
        request: fileRequest,
      } satisfies HttpBackendOptions,
      initImmediate: false, // Will wait the "request" to resolve
    });
  } catch (err) {
    const errorMessage = 'Failed to initialize i18next';

    void report({
      scope: EXO_SKELETON_SCOPE_NAME,
      type: REPORT_TYPES.error,
      value: err as Error | string,
      context: {
        language,
      },
    });
    throw new Error(errorMessage);
  }
};

export const setLocale = (locale: string) => {
  if (i18nInstance.language !== locale && supportedLocales.includes(locale)) {
    return i18nInstance.changeLanguage(locale);
  }
  return null;
};

export default i18nInstance;
